import React from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import TeamLogo from "../../_partials/TeamLogo";
import { PresetColors } from "../../../../shared/components/MediaLibrary/EditModal/LeftSidebar/EditTab";
import { getOneLetterResult } from "./helpers";
import matchTime from './images/match_info.webp';

// The seven season figures the comparison bars draw, in the order they stack.
// Each one is already pooled over Bundesliga, Champions League and DFB Pokal by
// the template's getDynamicData (fcb_matchday_slider_1/index.js in the
// webservice), so the values arrive ready to plot.
//
// What a full half of the bar is worth, per unit.
//
// The scale is shared by every stat measured in the same unit rather than set
// per row, and that is the whole point of it: points per game (~2) and shots
// per game (~15) are both counts per match, so drawing them against one
// ceiling is what makes the points bar visibly the shorter of the two. Give
// each row its own maximum instead and every row normalises into roughly the
// same length however different the numbers are -- the small ones stop reading
// as small.
//
// Stats in different units cannot share a scale: a possession percentage and a
// goals-per-game figure have no common length. So each unit gets one, and
// lengths are comparable within a unit, not across all seven.
//
// These are display ceilings, not records. Nudge them if the bars sit too full
// or too empty; a value at or past its ceiling simply fills the half.
const SCALES = {
  // Points, goals, shots and goals conceded -- all per match.
  perGame: 20,
  // Clean sheets: a running season total, so this bar grows all year where the
  // others hold steady. Drop it if the early-season rows read too short.
  seasonTotal: 15,
  percent: 100,
};

// `format` says how the number reads, `max` how long its bar runs.
const STAT_ROWS = [
  { key: "pointsPerGame", label: "Points per Game", max: SCALES.perGame },
  { key: "goalsPerGame", label: "Goals per Game", max: SCALES.perGame },
  { key: "shotsPerGame", label: "Shots per Game", max: SCALES.perGame },
  { key: "concededPerGame", label: "Conceded Goals per Game", max: SCALES.perGame },
  { key: "cleanSheets", label: "Clean Sheets", format: "count", max: SCALES.seasonTotal },
  { key: "possession", label: "Possession", format: "percent", max: SCALES.percent },
  { key: "duelsWonPercent", label: "Duels Won", format: "percent", max: SCALES.percent },
];

const formatStat = (value, format) => {
  const number = Number(value) || 0;
  if (format === "count") {
    return String(Math.round(number));
  }
  if (format === "percent") {
    return `${Math.round(number)}%`;
  }
  // Per-game figures: two decimals while they are small enough to need them,
  // one once they reach double digits (shots), so the column stays even.
  return number.toFixed(number < 10 ? 2 : 1);
};

// How much of its own half of the track one side fills, as a percentage of
// that half.
//
// The two halves meet on a fixed centre axis and grow outwards from it, each
// measured against the row's `max`. A stat whose values sit low against its
// scale draws a short pair of bars, one whose values sit high draws a long one
// -- so the rows differ by magnitude and not only by which side leads.
//
// Clamped, so a team past the scale fills its half rather than spilling out of
// it. No data draws nothing, which is the honest reading beside a 0.00.
const barFill = (value, max) => {
  const own = Number(value) || 0;
  if (!max || own <= 0) {
    return 0;
  }
  return Math.min((own / max) * 100, 100);
};

// Everything this template paints on top of its background layers.
//
// The editor canvas draws those layers itself as draggable boxes, so it renders
// this overlay alone (see EditModal/slider1Content.js) while the template's
// Index.jsx renders it over its own static copy of them. Both get the same
// markup from here, which is what stops the preview and the exported image
// drifting apart.
const SliderOverlay = ({ data, jersey, hasCLStars, isDfbPokal }) => {
  const { t } = useTranslation();

  const { awayLastGames, homeLastGames, seasonCompare } = data?.data?.dynamic || {};
  const homeTeam = data?.event?.home;
  const awayTeam = data?.event?.away;
  const competitionId = data?.event?.competitionId;
  const selectedLang = data?.lang;

  // Which competition artwork the frame carries, if any. Pokal is checked
  // first for the same reason it is on sliders 2 and 4: a cup tie is a cup tie
  // whatever else is toggled, and the two artworks replace each other rather
  // than stacking. `jersey` has no say here -- unlike slider 4, nothing in this
  // template's overlay is tinted per kit.
  const competitionClass = isDfbPokal ? "dfb" : hasCLStars ? "ucl" : null;

  return (
    <>
      <div className={`wc-content content`}>
      </div>

      <div className="match-time">
        <img
          src={matchTime}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      </div>

      {/* The two crests, directly under the kick-off block and in the same
          column. Guarded on the pair rather than on each side: `event.home` and
          `event.away` are resolved together, and one crest beside an empty box
          reads worse than none -- the same call the stats block below makes. */}
      {homeTeam && awayTeam && (
        <div className="match-teams">
          <div className="home-team-logo-container">
            <TeamLogo team={homeTeam} competitionId={competitionId} />
          </div>
          <div className="away-team-logo-container">
            <TeamLogo team={awayTeam} competitionId={competitionId} />
          </div>
        </div>
      )}

      {/* Guarded on the block rather than on its two sides: getDynamicData
        returns `seasonCompare: null` when it could not resolve the teams or the
        pooled competitions, and half a set of bars reads worse than none. */}
      {seasonCompare && (
        <div className="season-compare">
          {STAT_ROWS.map(({ key, label, format, max }) => {
            const home = seasonCompare?.home?.[key];
            const away = seasonCompare?.away?.[key];

            return (
              <div className="stat-row" key={key}>
                {/* Their own `table.slider` namespace rather than `table`
                  itself, so these seven long labels sit apart from the short
                  column headings the tables use. */}
                <div className="stat-name">
                  {t(label, { keyPrefix: "table.slider", lng: selectedLang })}
                </div>
                <div className="stat-values">
                  <span className="home">{formatStat(home, format)}</span>
                  <span className="away">{formatStat(away, format)}</span>
                </div>
                <div className="stat-bar">
                  <div className="home">
                    <span style={{ width: `${barFill(home, max)}%` }} />
                  </div>
                  <div className="away">
                    <span style={{ width: `${barFill(away, max)}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="first-bottom-container">
        <div className={`${selectedLang === 'ar' ? 'logo-container-ar' : 'logo-container'}`}>

        </div>
        <div className={`last-games`}>


          {/* <div className="home-away-container">
          <div className={`home`}>
            <TeamLogo team={homeTeam} competitionId={competitionId} />
          </div>

          <div className={`away`}>
            <TeamLogo team={awayTeam} competitionId={competitionId} />
          </div>
        </div> */}


        </div>
      </div>
      <div className="gradient"></div>
      {/* Drawn after `.gradient` deliberately. Both sit at z-index 0, so DOM
          order decides, and the competition artwork belongs over the bottom
          scrim rather than under it -- it is part of the look, not something
          the ramp is meant to darken. Still below the content, which is at 1.
          It lives here rather than beside the background layers so the editor
          preview picks it up from the same component the export renders. */}
      {competitionClass === "ucl" && <div className="ucl-gradient"></div>}
      {competitionClass === "dfb" && <div className="dfb-gradient"></div>}
    </>
  );
};

export default SliderOverlay;
